import { type PropsWithChildren } from 'react';
import { Navigate } from 'react-router';

import { ROUTES } from '~shared/config/routes';
import { Loader } from '~shared/ui/loader';

import { useUserInfo } from '../api';
import {
  type Permission,
  isPagePermission,
  useGetPermissions,
} from '../entities/permissions';
import { hasAllPermissions, useIdentifyUser } from '../lib';

interface PermissionGuardProps {
  permissions: Permission[] | 'none-of' | 'none-of-pages';
  redirectTo?: string;
}

const hasAccess = (
  granted: Permission[],
  required: Permission[] | 'none-of' | 'none-of-pages'
) => {
  if (required === 'none-of') {
    return granted.length === 0;
  }

  if (required === 'none-of-pages') {
    return !granted.some(isPagePermission);
  }

  return hasAllPermissions(granted, required);
};

export const PermissionsGuard = ({
  children,
  permissions,
  redirectTo = ROUTES.root,
}: PropsWithChildren<PermissionGuardProps>) => {
  const userInfoQuery = useUserInfo();
  const permissionsQuery = useGetPermissions();

  useIdentifyUser(userInfoQuery.data, permissionsQuery.data);

  if (!userInfoQuery.data || !permissionsQuery.data) {
    return <Loader />;
  }

  if (hasAccess(permissionsQuery.data, permissions)) {
    return children;
  }

  return <Navigate to={redirectTo} replace />;
};
